import {otsBaseApi} from "../../ots-api";
import {Well} from "../../../model/well";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Wells", "SiteWells", "ContainerClusterWells", "ContainerWells"]});

export const wellApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllWells: builder.query<Well[], string | undefined>({
      query: (regionId) => ({
        url: `/well/all`,
        method: "GET",
        params: regionId ? {regionId: regionId} : undefined
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Wells", id} as const)),
            {type: "Wells", id: "LIST"}
          ];
        } else {
          return [{type: "Wells", id: "LIST"}];
        }
      }
    }),
    getWellsBySite: builder.query<
      Well[],
      { regionId: string, siteId: string }
    >({
      query: ({regionId, siteId}) => ({
        url: "/wells/search",
        method: "POST",
        body: {
          regionIds: [regionId],
          siteIds: [siteId]
        }
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Wells", id} as const)
            ),
            {type: "SiteWells", id: arg.siteId}
          ];
        } else {
          return [];
        }
      }
    }),
    getWellsByContainer: builder.query<
      Well[],
      { regionId: string, containerId: string }
    >({
      query: ({regionId, containerId}) => ({
        url: "/wells/search",
        method: "POST",
        body: {
          regionIds: [regionId],
          containerIds: [containerId]
        }
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Wells", id} as const)
            ),
            {type: "ContainerWells", id: arg.containerId}
          ];
        } else {
          return [];
        }
      }
    }),
    getWellsByContainerCluster: builder.query<
      Well[],
      { regionId: string, containerClusterId: string }
    >({
      query: ({regionId, containerClusterId}) => ({
        url: "/wells/search",
        method: "POST",
        body: {
          regionIds: [regionId],
          containerClusterIds: [containerClusterId]
        }
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Wells", id} as const)
            ),
            {type: "ContainerClusterWells", id: arg.containerClusterId}
          ];
        } else {
          return [];
        }
      }
    }),
    getWell: builder.query<Well, string>({
      query: (id) => ({
        url: `/well/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Wells", id}]
    }),
    createWell: builder.mutation<Well, Partial<Well>>({
      query: (well) => ({
        url: '/well',
        method: 'POST',
        body: well,
      }),
      invalidatesTags: [
        {type: 'Wells', id: 'LIST'},
        {type: "SiteWells"},
        {type: "ContainerWells"},
        {type: "ContainerClusterWells"},
      ],
    }),
    updateWell: builder.mutation<Well, Partial<Well>>({
      query: (well) => ({
        url: '/well',
        method: 'PUT',
        body: well,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Wells', id}],
    }),
    deleteWell: builder.mutation<void, Partial<Well>>({
      query: (well) => ({
        url: '/well',
        method: 'DELETE',
        body: well,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Wells', id}],
    }),
  })
});

export const {
  useGetAllWellsQuery,
  useGetWellsBySiteQuery,
  useGetWellsByContainerQuery,
  useGetWellsByContainerClusterQuery,
  useGetWellQuery,
  useCreateWellMutation,
  useUpdateWellMutation,
  useDeleteWellMutation,
} = wellApi;
